import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import { format, parseISO } from 'date-fns';
import { FormControl, Select, MenuItem, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ProductSelector from './ProductSelector';
import '../styles/PostAnalytics.css';
import { apiPost } from '../services/apiClient';
import { fillBucketGaps } from '../utils/timeSeriesBucketing';
import { useStockSeries } from '../hooks/useStockSeries';
import { useStockPremium } from '../hooks/useStockPremium';
import {
    mergeStockIntoRows, mergePremiumIntoRows, benchmarkLabel, periodForMonths,
    STOCK_COLOR, STOCK_DASH_STRING, PREMIUM_MODES,
    formatStockPrice, formatPremiumPct,
} from '../utils/stockOverlay';
import { formatCompactNumber, formatFullNumber } from '../utils/formatNumber';

const DEFAULT_SOURCE_TYPES = [
    { value: 'all', label: 'All Sources' }
];

const ROLLING_AVG_OPTIONS = [
    { value: 5, label: '5 Days' },
    { value: 14, label: '14 Days' },
    { value: 30, label: '30 Days' },
    { value: 60, label: '60 Days' },
    { value: 90, label: '90 Days' },
    { value: 180, label: '180 Days' },
    { value: 365, label: '365 Days' }
];

const BUCKET_PERIOD_OPTIONS = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' }
];

const PostAnalytics = ({ topicMap, companyName, companyTicker, autoSearch = false,
    initialStartDate, initialEndDate, initialBucket, initialSource, initialRollingDays,
    // Title/label overrides so the same chart can read as "Videos Published"
    // etc. Defaults preserve the original "Post Volume" behavior.
    chartTitle = 'Post Volume', valueAxisLabel = 'Number of Posts', barName = 'Post Count',
    // Embedded mode: hide this chart's own control bar and drive it from a shared
    // parent control bar. startDate/endDate/bucketPeriod/source become controlled
    // props and the chart auto-fetches on any change (no per-chart Search button).
    embedded = false,
    startDate: controlledStartDate, endDate: controlledEndDate,
    bucketPeriod: controlledBucketPeriod, sourceType: controlledSourceType,
    // Zero-fill empty buckets across the range (opt-in) and shrink the plot so
    // several charts stack into view at once.
    fillEmptyBuckets = false, chartHeight = 500,
    // Compact the value-axis ticks (1.2K / 5M) and show full numbers in the
    // tooltip. Opt-in so shared uses (Daily Trackers) are unchanged; the
    // YouTube Analytics page sets it for large view counts.
    compactYAxis = false,
    // Optional controlled product selection, so a parent (e.g. the Daily
    // Trackers tab switcher) can keep the selected topic sticky across
    // different chart types. Falls back to internal state when omitted.
    selectedProduct: controlledSelectedProduct, onProductChange }) => {
    const [data, setData] = useState([]);
    // Overlay mode: 'off' | 'price' | 'peers' | 'snp500' | 'sector'.
    const [overlayMode, setOverlayMode] = useState('off');
    const [internalSelectedProduct, setInternalSelectedProduct] = useState('');
    const isProductControlled = controlledSelectedProduct !== undefined && typeof onProductChange === 'function';
    const selectedProduct = isProductControlled ? controlledSelectedProduct : internalSelectedProduct;
    const setSelectedProduct = isProductControlled ? onProductChange : setInternalSelectedProduct;
    const [availableSources, setAvailableSources] = useState(DEFAULT_SOURCE_TYPES);
    const [internalSourceType, setSourceType] = useState(initialSource || 'all');
    const sourceType = controlledSourceType !== undefined ? controlledSourceType : internalSourceType;
    const [rollingDays, setRollingDays] = useState(initialRollingDays || (embedded ? 90 : 5));
    const [internalBucketPeriod, setBucketPeriod] = useState(initialBucket || 'day');
    const bucketPeriod = controlledBucketPeriod !== undefined ? controlledBucketPeriod : internalBucketPeriod;
    const [isLoading, setIsLoading] = useState(false);

    // Initialize dates
    const today = new Date();
    const defaultStartDate = new Date('2025-06-25');

    const [internalStartDate, setStartDate] = useState(initialStartDate || defaultStartDate.toISOString().split('T')[0]);
    const startDate = controlledStartDate !== undefined ? controlledStartDate : internalStartDate;
    const [internalEndDate, setEndDate] = useState(initialEndDate || today.toISOString().split('T')[0]);
    const endDate = controlledEndDate !== undefined ? controlledEndDate : internalEndDate;
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    // Add "All Topics" option to the topicMap
    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};

        // Auto-select first available product if none selected
        if (Object.keys(topicMap).length > 0 && !selectedProduct) {
            const sortedTopics = Object.values(topicMap).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB;
            });

            if (sortedTopics[0]) {
                setSelectedProduct(sortedTopics[0].id);
            }
        }

        // Add "All Topics" option
        return {
            ...topicMap,
            'all': {
                id: 'all',
                name: 'All Topics',
                priority: 9999
            }
        };
    }, [topicMap, selectedProduct]);

    const getSelectedProductName = () => {
        if (selectedProduct && filteredTopicMap && filteredTopicMap[selectedProduct]) {
            return filteredTopicMap[selectedProduct].name;
        }
        return 'Product';
    };

    const getSourceLabel = () => {
        const source = availableSources.find(s => s.value === sourceType);
        return source ? source.label : 'All Sources';
    };

    const getBucketLabel = () => {
        const bucket = BUCKET_PERIOD_OPTIONS.find(b => b.value === bucketPeriod);
        return bucket ? bucket.label : 'Daily';
    };

    const buildMainTitle = () => {
        return `${getSelectedProductName()} · ${chartTitle}`;
    };

    const buildSubline = () => {
        const dateRange = `${format(parseISO(startDate), 'MMM d, yyyy')} - ${format(parseISO(endDate), 'MMM d, yyyy')}`;
        return `${dateRange} • ${getSourceLabel()} • ${getBucketLabel()}`;
    };

    // ProductSelector handles auto-selection internally

    // Fetch available sources when product changes
    useEffect(() => {
        const fetchAvailableSources = async () => {
            if (!selectedProduct) {
                setAvailableSources(DEFAULT_SOURCE_TYPES);
                return;
            }

            try {
                let topicIdToFetch = selectedProduct;

                if (selectedProduct === 'all') {
                    const actualTopics = Object.values(topicMap).filter(t => t.id !== 'all');
                    if (actualTopics.length === 0) {
                        setAvailableSources(DEFAULT_SOURCE_TYPES);
                        return;
                    }
                    const sortedTopics = actualTopics.sort((a, b) => {
                        const priorityA = a.priority === undefined ? Infinity : a.priority;
                        const priorityB = b.priority === undefined ? Infinity : b.priority;
                        return priorityA - priorityB;
                    });
                    topicIdToFetch = sortedTopics[0].id;
                }

                const endTime = Math.floor(new Date(endDate).getTime() / 1000);
                const startTime = Math.floor(new Date(startDate).getTime() / 1000);

                const response = await apiPost('topic/assessments/available-sources', {
                    topic_id: topicIdToFetch,
                    assessment_type: 'NPS',
                    start_time: startTime,
                    end_time: endTime
                });

                if (!response.ok) throw new Error(`Backend error: ${response.status}`);

                const json = await response.json();
                const data = json.data ?? json ?? [];

                const sourceOptions = [
                    { value: 'all', label: 'All Sources' },
                    ...data
                ];

                setAvailableSources(sourceOptions);
            } catch (error) {
                console.error('Error fetching available sources:', error);
                setAvailableSources(DEFAULT_SOURCE_TYPES);
            }
        };

        fetchAvailableSources();
    }, [selectedProduct, startDate, endDate, topicMap]);

    // Fetch post data - only called when user clicks Search
    const handleSearch = useCallback(async () => {
        if (!selectedProduct) return;

        setIsLoading(true);
        try {
            let rawData = [];

            if (selectedProduct === 'all') {
                // Fetch data for all topics and aggregate
                console.log('Fetching data for all topics...');
                const topicIds = Object.keys(topicMap);

                // Fetch data for each topic via backend
                const allTopicData = await Promise.all(
                    topicIds.map(async (topicId) => {
                        try {
                            const response = await apiPost('topic/posts/get-daily-posts', {
                                topic_id: topicId,
                                start_date: startDate,
                                end_date: endDate,
                                source_type: sourceType === 'all' ? null : sourceType,
                            });

                            if (!response.ok) {
                                throw new Error(`Backend error: ${response.status}`);
                            }

                            const json = await response.json();
                            return json.data ?? json ?? [];
                        } catch (error) {
                            console.error(`Error fetching data for topic ${topicId}:`, error);
                            return [];
                        }
                    })
                );

                // Aggregate data by date
                const aggregatedData = {};
                allTopicData.forEach(topicData => {
                    if (!Array.isArray(topicData)) return;

                    topicData.forEach(item => {
                        const date = item.day;
                        const numPosts = typeof item.num_posts === 'bigint'
                            ? Number(item.num_posts.toString())
                            : Number(item.num_posts);

                        if (!aggregatedData[date]) {
                            aggregatedData[date] = {
                                day: date,
                                num_posts: 0
                            };
                        }

                        aggregatedData[date].num_posts += numPosts;
                    });
                });

                // Convert aggregated data back to array format
                rawData = Object.values(aggregatedData);
                console.log('Aggregated data for all topics:', rawData);

            } else {
                // Single topic fetch via backend
                const response = await apiPost('topic/posts/get-daily-posts', {
                    topic_id: selectedProduct,
                    start_date: startDate,
                    end_date: endDate,
                    source_type: sourceType === 'all' ? null : sourceType,
                });

                if (!response.ok) {
                    throw new Error(`Backend error: ${response.status}`);
                }

                const json = await response.json();
                rawData = json.data ?? json ?? [];
            }

            console.log('Raw data received:', rawData);

            if (!Array.isArray(rawData) || rawData.length === 0) {
                console.log('No data received or data is empty');
                setData([]);
                return;
            }

            // Process the data - handle bigint by using toString() first
            const processedData = rawData.map(item => {
                console.log('Processing item:', item);
                // Handle potential bigint by converting to string first
                const numPosts = typeof item.num_posts === 'bigint'
                    ? Number(item.num_posts.toString())
                    : Number(item.num_posts);

                return {
                    date: format(parseISO(item.day), 'MMM dd'),
                    fullDate: item.day, // Keep full date for sorting
                    postCount: numPosts
                };
            });

            // Sort data by date
            processedData.sort((a, b) => {
                const dateA = parseISO(a.fullDate);
                const dateB = parseISO(b.fullDate);
                return dateA - dateB;
            });

            // Aggregate data based on bucket period
            let aggregatedData = processedData;

            if (bucketPeriod === 'week') {
                // Group by week
                const weeklyData = [];
                let currentWeek = [];
                let currentWeekStart = null;

                processedData.forEach(item => {
                    const itemDate = parseISO(item.fullDate);
                    const weekStart = new Date(itemDate);
                    weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Start of week (Sunday)

                    if (!currentWeekStart || weekStart.getTime() !== currentWeekStart.getTime()) {
                        if (currentWeek.length > 0) {
                            const totalPosts = currentWeek.reduce((sum, day) => sum + day.postCount, 0);
                            weeklyData.push({
                                date: format(currentWeekStart, 'MMM dd'),
                                fullDate: format(currentWeekStart, 'yyyy-MM-dd'),
                                postCount: totalPosts
                            });
                        }
                        currentWeek = [item];
                        currentWeekStart = weekStart;
                    } else {
                        currentWeek.push(item);
                    }
                });

                // Don't forget the last week
                if (currentWeek.length > 0) {
                    const totalPosts = currentWeek.reduce((sum, day) => sum + day.postCount, 0);
                    weeklyData.push({
                        date: format(currentWeekStart, 'MMM dd'),
                        fullDate: format(currentWeekStart, 'yyyy-MM-dd'),
                        postCount: totalPosts
                    });
                }

                aggregatedData = weeklyData;

            } else if (bucketPeriod === 'month') {
                // Group by month
                const monthlyData = [];
                let currentMonth = [];
                let currentMonthKey = null;

                processedData.forEach(item => {
                    const itemDate = parseISO(item.fullDate);
                    const monthKey = format(itemDate, 'yyyy-MM');

                    if (currentMonthKey !== monthKey) {
                        if (currentMonth.length > 0) {
                            const totalPosts = currentMonth.reduce((sum, day) => sum + day.postCount, 0);
                            const firstDayOfMonth = parseISO(currentMonth[0].fullDate);
                            monthlyData.push({
                                date: format(firstDayOfMonth, 'MMM yyyy'),
                                fullDate: format(firstDayOfMonth, 'yyyy-MM-dd'),
                                postCount: totalPosts
                            });
                        }
                        currentMonth = [item];
                        currentMonthKey = monthKey;
                    } else {
                        currentMonth.push(item);
                    }
                });

                // Don't forget the last month
                if (currentMonth.length > 0) {
                    const totalPosts = currentMonth.reduce((sum, day) => sum + day.postCount, 0);
                    const firstDayOfMonth = parseISO(currentMonth[0].fullDate);
                    monthlyData.push({
                        date: format(firstDayOfMonth, 'MMM yyyy'),
                        fullDate: format(firstDayOfMonth, 'yyyy-MM-dd'),
                        postCount: totalPosts
                    });
                }

                aggregatedData = monthlyData;
            }

            // Zero-fill empty buckets (opt-in) before the rolling average so it
            // runs across the zeros and every bucket in range gets a bar.
            if (fillEmptyBuckets) {
                aggregatedData = fillBucketGaps(aggregatedData, startDate, endDate, bucketPeriod, { postCount: 0 });
            }

            // Calculate rolling average based on selected period
            const dataWithRollingAvg = aggregatedData.map((item, index) => {
                // Adjust rolling window based on bucket period
                let windowSize = rollingDays;
                if (bucketPeriod === 'week') {
                    windowSize = Math.ceil(rollingDays / 7);
                } else if (bucketPeriod === 'month') {
                    windowSize = Math.ceil(rollingDays / 30);
                }

                // Get the last N periods (including current period)
                const startIndex = Math.max(0, index - (windowSize - 1));
                const rollingWindow = aggregatedData.slice(startIndex, index + 1);

                // Calculate average
                const sum = rollingWindow.reduce((acc, dayData) => acc + dayData.postCount, 0);
                const average = sum / rollingWindow.length;

                return {
                    ...item,
                    rollingAvg: Math.round(average * 10) / 10 // Round to 1 decimal place
                };
            });

            console.log('Final processed data with rolling average:', dataWithRollingAvg);
            setData(dataWithRollingAvg);
        } catch (error) {
            console.error('Error in fetchData:', error);
            setData([]); // Set empty data on error
        } finally {
            setIsLoading(false);
        }
    }, [selectedProduct, startDate, endDate, rollingDays, bucketPeriod, topicMap, sourceType, fillEmptyBuckets]);

    // Capture mode: fire the search once automatically (no manual click) so the
    // chart renders when this component is mounted off-screen for the Report
    // Generator. Has no effect on the live page unless `autoSearch` is set.
    const autoSearchedRef = useRef(false);
    useEffect(() => {
        if (autoSearch && selectedProduct && !autoSearchedRef.current) {
            autoSearchedRef.current = true;
            handleSearch();
        }
    }, [autoSearch, selectedProduct, handleSearch]);

    // Embedded mode: auto-fetch whenever the shared inputs change (no Search click).
    useEffect(() => {
        if (embedded && selectedProduct) handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embedded, selectedProduct, startDate, endDate, bucketPeriod, sourceType]);

    // Stock overlay (publicly-traded companies only). 'price' shows the absolute
    // adjusted-close line; 'peers' / 'snp500' / 'sector' show a relative-
    // performance ("premium") comparison: the company and its benchmark indexed
    // to 100 at the start of the visible window.
    const priceMode = overlayMode === 'price';
    const premiumMode = ['peers', 'snp500', 'sector'].includes(overlayMode) ? overlayMode : null;

    // Request the premium window that matches the chart's visible date range so a
    // backend (period-start-baselined) premium lines up with what's shown.
    const premiumPeriod = useMemo(
        () => periodForMonths(Math.round((new Date(endDate) - new Date(startDate)) / (30 * 86400000))),
        [startDate, endDate]
    );
    const { series: stockSeries } = useStockSeries(priceMode ? companyTicker : null);
    const { payload: premium, error: premiumError } = useStockPremium(companyTicker, premiumMode, premiumPeriod);

    const showStockOverlay = Boolean(companyTicker) && priceMode;
    const showPremiumOverlay = Boolean(companyTicker) && Boolean(premiumMode) && Boolean(premium?.data?.length);
    const benchLabel = premium ? benchmarkLabel(premium) : '';
    const premiumName = premium ? `${companyTicker} vs ${benchLabel}` : '';

    const chartData = useMemo(() => {
        if (showStockOverlay) return mergeStockIntoRows(data, stockSeries, bucketPeriod);
        if (showPremiumOverlay) return mergePremiumIntoRows(data, premium.data, bucketPeriod);
        return data;
    }, [data, stockSeries, premium, bucketPeriod, showStockOverlay, showPremiumOverlay]);

    // Add debug log for render
    console.log('Rendering with data:', data);

    // Consistent control height for all dropdowns
    const CONTROL_HEIGHT = 36;
    const CONTROL_GAP = 12;

    const selectStyle = {
        width: '100%',
        height: CONTROL_HEIGHT,
        '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingRight: '24px !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#999',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
        }
    };

    const filterLabelStyle = {
        fontSize: '9px',
        fontWeight: 600,
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '3px'
    };

    const dateInputStyle = {
        padding: '6px 10px',
        borderRadius: '4px',
        border: '1px solid #d0d0d0',
        fontSize: '13px',
        height: CONTROL_HEIGHT,
        boxSizing: 'border-box',
        transition: 'border-color 0.15s ease',
        outline: 'none',
        width: '100%'
    };

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Header Card */}
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    padding: '20px 24px 18px',
                    marginBottom: '20px',
                    boxShadow: isHeaderHovered
                        ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                        : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    transition: 'box-shadow 0.2s ease',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* Title Block */}
                <div style={{ marginBottom: '12px' }}>
                    {/* Company Name - lightest */}
                    <div style={{
                        fontSize: '11px',
                        color: '#777',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        marginBottom: '1px'
                    }}>
                        {companyName || 'Company'}
                    </div>

                    {/* Main H1 */}
                    <h1 style={{
                        fontSize: '22px',
                        fontWeight: 700,
                        margin: 0,
                        color: '#111',
                        lineHeight: 1.25
                    }}>
                        {selectedProduct ? buildMainTitle() : 'Select a Product'}
                    </h1>

                    {/* Context Line - lighter gray */}
                    {selectedProduct && (
                        <div style={{
                            fontSize: '12px',
                            color: '#999',
                            lineHeight: 1.3,
                            marginTop: '2px'
                        }}>
                            {buildSubline()}
                        </div>
                    )}
                </div>

                {/* Filter Bar (hidden in embedded mode — shared controls live in the parent) */}
                {!embedded && (
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: `${CONTROL_GAP}px`,
                    flexWrap: 'wrap',
                    rowGap: '12px'
                }}>
                    {/* Product */}
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 160, boxSizing: 'border-box' }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct || ''}
                            onSelect={setSelectedProduct}
                            label=""
                            size="small"
                            sx={selectStyle}
                        />
                    </div>

                    {/* Start Date */}
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>Start Date</div>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={endDate}
                            style={dateInputStyle}
                        />
                    </div>

                    {/* End Date */}
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>End Date</div>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={new Date().toISOString().split('T')[0]}
                            style={dateInputStyle}
                        />
                    </div>

                    {/* Source */}
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 150 }}>
                        <div style={filterLabelStyle}>Source</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={sourceType}
                                onChange={(e) => setSourceType(e.target.value)}
                            >
                                {availableSources.map(source => (
                                    <MenuItem key={source.value} value={source.value}>
                                        {source.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Rolling Average */}
                    <div style={{ flex: '1 1 auto', minWidth: 90, maxWidth: 120 }}>
                        <div style={filterLabelStyle}>Rolling Avg</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={rollingDays}
                                onChange={(e) => setRollingDays(Number(e.target.value))}
                            >
                                {ROLLING_AVG_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Bucket Period */}
                    <div style={{ flex: '1 1 auto', minWidth: 80, maxWidth: 110 }}>
                        <div style={filterLabelStyle}>Bucket</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={bucketPeriod}
                                onChange={(e) => setBucketPeriod(e.target.value)}
                            >
                                {BUCKET_PERIOD_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Stock overlay (publicly-traded companies only): absolute
                        price, or a relative-performance comparison vs peers /
                        S&P 500 / sector. */}
                    {companyTicker && (
                        <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                            <div style={filterLabelStyle}>Overlay</div>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    value={overlayMode}
                                    onChange={(e) => setOverlayMode(e.target.value)}
                                    sx={{ height: CONTROL_HEIGHT }}
                                >
                                    <MenuItem value="off">Off</MenuItem>
                                    {PREMIUM_MODES.map((m) => (
                                        <MenuItem key={m.value} value={m.value}>
                                            {m.value === 'price' ? `Price (${companyTicker})` : m.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {premiumMode && premiumError && (
                                <div style={{ fontSize: '11px', color: '#b00', marginTop: '2px', maxWidth: 140 }}>
                                    No comparison available
                                </div>
                            )}
                        </div>
                    )}

                    {/* Search Button */}
                    <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={!selectedProduct || isLoading}
                            startIcon={<SearchIcon />}
                            sx={{
                                height: CONTROL_HEIGHT,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                            }}
                        >
                            {isLoading ? 'Loading...' : 'Search'}
                        </Button>
                    </div>
                </div>
                )}
            </div>

            {/* Chart Section */}
            <div style={{ marginTop: '8px', width: '100%', height: `${chartHeight}px` }}>
                <ResponsiveContainer>
                    <ComposedChart data={chartData} margin={{ left: 20 }}>

                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            angle={-45}
                            textAnchor="end"
                            height={60}
                        />
                        <YAxis
                            yAxisId="left"
                            tickFormatter={compactYAxis ? formatCompactNumber : undefined}
                            label={{
                                value: valueAxisLabel,
                                angle: -90,
                                position: 'insideLeft',
                                dx: -10,
                                style: { textAnchor: 'middle' }
                            }}
                        />
                        {showStockOverlay && (
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={['auto', 'auto']}
                                tickFormatter={formatStockPrice}
                                label={{
                                    value: 'Stock Price ($)',
                                    angle: 90,
                                    position: 'insideRight'
                                }}
                            />
                        )}
                        {showPremiumOverlay && (
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={([dataMin, dataMax]) => [Math.min(0, dataMin), Math.max(0, dataMax)]}
                                tickFormatter={(v) => `${Math.round(v)}%`}
                                label={{
                                    value: 'Premium (%)',
                                    angle: 90,
                                    position: 'insideRight'
                                }}
                            />
                        )}
                        <Tooltip formatter={(value, name) => (
                            name === `Stock Price (${companyTicker})`
                                ? formatStockPrice(value)
                                : name === premiumName
                                    ? formatPremiumPct(value)
                                    : compactYAxis ? formatFullNumber(value) : value
                        )} />
                        <Legend />
                        <Bar
                            yAxisId="left"
                            dataKey="postCount"
                            fill="#8884d8"
                            name={barName}
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="rollingAvg"
                            stroke="#000000"
                            strokeWidth={2}
                            name={`${rollingDays}-Day Rolling Average`}
                            dot={{ fill: '#808080', strokeWidth: 1, r: 2 }}
                            activeDot={{ r: 6, stroke: '#ff7300', strokeWidth: 1 }}
                        />
                        {showStockOverlay && (
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="stockPrice"
                                stroke={STOCK_COLOR}
                                strokeWidth={2}
                                strokeDasharray={STOCK_DASH_STRING}
                                name={`Stock Price (${companyTicker})`}
                                dot={false}
                                connectNulls
                            />
                        )}
                        {showPremiumOverlay && (
                            <ReferenceLine yAxisId="right" y={0} stroke="#bbb" strokeDasharray="3 3" />
                        )}
                        {showPremiumOverlay && (
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="premiumPct"
                                stroke={STOCK_COLOR}
                                strokeWidth={2}
                                strokeDasharray={STOCK_DASH_STRING}
                                name={premiumName}
                                dot={false}
                                connectNulls
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default PostAnalytics; 